package uz.mint.frontend.i18n

/**
 * Every user-visible string on the site, one table per [Locale].
 *
 * Keeping this as a plain data class rather than a key→string map means a missing translation
 * is a compile error, not a blank space discovered in production.
 *
 * Copy comes from the *Mint Landing.dc.html* design. The design translates its chrome into UZ
 * and EN but leaves the legal documents in Russian only — see [legal] for why that stands.
 */
data class SiteStrings(
    val metaTitle: String,
    val metaDescription: String,

    val navPlans: String,
    val navHow: String,
    val navSupport: String,
    val navCta: String,

    /** Sits before the phone number in the strip above the nav. */
    val topPhoneLabel: String,

    val heroEyebrow: String,
    val heroTitle1: String,
    val heroTitle2: String,
    val heroLede: String,
    val heroCta1: String,
    val heroCta2: String,
    val heroPoster: String,

    val finderTitle: String,
    val finderSub: String,
    val finderPlaceholder: String,
    val finderNone: String,
    val finderAll: String,

    val stat1Value: String,
    val stat1: String,
    val stat2Value: String,
    val stat2: String,
    val stat3Value: String,
    val stat3: String,

    val tariffsTitle: String,
    val tariffsSub: String,
    val tariffsFrom: String,

    /**
     * The destination picker — the storefront's first step.
     *
     * These replaced the three category tabs (локальные / региональные / глобальные), which asked
     * a customer to classify their own trip against the supplier's taxonomy before they could see
     * a price. The only surviving trace of that split is [destAllCountries] versus [destRegions],
     * which is a distinction between places rather than between bundle types.
     */
    val destPopular: String,
    val destAllCountries: String,
    val destRegions: String,
    val destSearchPlaceholder: String,
    val destNoMatch: String,
    val destLoading: String,
    val destError: String,
    /** "← Все направления" — back out of a chosen destination to the picker. */
    val destBack: String,
    /** "{n} тарифов" on a destination card. Same numeral-agreement problem as [dayForms]. */
    val destPlanCountTemplate: String,
    /** "Пакеты: Турция" — the heading once a destination has been chosen. */
    val destChosenTemplate: String,
    /** Link under the landing page's short list, through to every country. */
    val destSeeAll: String,
    val tariffsNote: String,
    val tariffsAll: String,
    val tariffsLoading: String,
    val tariffsEmpty: String,
    val tariffsError: String,
    val tariffsRetry: String,

    val plansMetaTitle: String,
    val plansMetaDescription: String,
    val plansTitle: String,
    val plansLede: String,
    /** "{n} тарифов в каталоге" — replaces [plansLede] once the first page has arrived. */
    val plansCountTemplate: String,
    val plansBack: String,
    val plansPrev: String,
    val plansNext: String,
    /** "Страница {0} из {1}". */
    val plansPageTemplate: String,

    val checkoutTitle: String,
    val checkoutData: String,
    val checkoutValidity: String,
    val checkoutCode: String,
    val checkoutTotal: String,
    /** What happens after paying: the eSIM is issued automatically, usually within a minute. */
    val checkoutHandoff: String,
    /**
     * "Оплатить через {gateway}" — the cashier's brand name is substituted at render time.
     *
     * A template rather than one string per provider: which cashiers exist is a deployment
     * decision the backend answers, so a hardcoded "Click" here is wrong the moment an operator
     * turns one off.
     */
    val checkoutPay: String,
    /**
     * The first button, before any cashier is named.
     *
     * Which cashiers exist is only known once the order has been created — each link carries
     * that order's reference — so the choice cannot be offered before this is pressed.
     */
    val checkoutProceed: String,
    /** Heading over the cashier list, shown only when there is more than one to choose from. */
    val checkoutChoose: String,
    val checkoutPaying: String,
    val checkoutUnavailable: String,
    /** Shown to a logged-out visitor: the order needs an owner before it can be paid for. */
    val checkoutSignInNote: String,
    val checkoutSignIn: String,
    /** Failure to create the order, distinct from failing to load the plan. */
    val checkoutOrderError: String,
    val checkoutTelegram: String,
    val checkoutLoading: String,
    val checkoutError: String,

    val unitGb: String,
    val unitMb: String,
    val currencyUzs: String,
    /**
     * Stand-in for a price the backend could not convert — no USD→UZS rate is configured, so
     * there is no sum to print. Prices are shown in so'm only, so there is no dollar figure to
     * fall back to; inventing a rate would misprice the whole catalogue.
     */
    val priceUnavailable: String,
    val dayForms: PluralForms,
    /** "1 тариф / 2 тарифа / 5 тарифов" — same numeral agreement problem as [dayForms]. */
    val planForms: PluralForms,

    val advTitle: String,
    val advantages: List<Advantage>,

    val ctaTitle1: String,
    val ctaTitle2: String,
    val ctaSub: String,
    val ctaBtn: String,

    val footerAbout: String,
    val footerC1: String,
    val footerL1: String,
    val footerL2: String,
    val footerC2: String,
    val footerL4: String,
    val footerL5: String,
    val footerL6: String,
    val footerC3: String,
    val footerRights: String,
    val footerTerms: String,
    val footerPrivacy: String,

    val faqTitle: String,
    val faqMetaDescription: String,
    val legalOffer: String,
    val legalPrivacy: String,
    val legalClose: String,
) {
    data class Advantage(val title: String, val body: String)

    /**
     * "2 884 тарифа в каталоге". The number is grouped and the noun agreed, which is why this is
     * a template rather than concatenation at the call site — Russian would otherwise read
     * "2884 тарифов" on the locale most visitors land on.
     */
    fun plansCount(locale: Locale, total: Int): String =
        plansCountTemplate.withCount(locale, total, planForms)

    /** "17 тарифов" on a destination card. */
    fun destPlanCount(locale: Locale, count: Int): String =
        destPlanCountTemplate.withCount(locale, count, planForms)

    /** "Пакеты: Турция". */
    fun destChosen(name: String): String = destChosenTemplate.replace("{destination}", name)

    /** "Страница 3 из 121". */
    fun plansPageOf(page: Int, totalPages: Int): String =
        plansPageTemplate.replace("{0}", page.toString()).replace("{1}", totalPages.toString())

    private fun String.withCount(locale: Locale, count: Int, forms: PluralForms): String =
        replace("{n}", groupThousands(count)).replace("{noun}", locale.plural(count, forms))
}

/** "2 884" — a bare four-digit count reads as a price otherwise. */
private fun groupThousands(value: Int): String {
    val digits = value.toString()
    return buildString {
        digits.forEachIndexed { index, char ->
            if (index > 0 && (digits.length - index) % 3 == 0) append(' ')
            append(char)
        }
    }
}

private val Russian = SiteStrings(
    metaTitle = "Mint.uz — eSIM для путешествий",
    metaDescription = "eSIM для поездок за границу: интернет в 180 странах, большие пакеты по низкой цене, без роуминга и пластиковых SIM-карт.",

    navPlans = "Тарифы",
    navHow = "Как это работает",
    navSupport = "Поддержка",
    navCta = "Подобрать eSIM",
    topPhoneLabel = "Поддержка:",

    heroEyebrow = "eSIM для поездок",
    heroTitle1 = "Интернет за границей",
    heroTitle2 = "без роуминга",
    heroLede = "Подключите тариф до вылета. Одна eSIM работает в 180 странах — без физической SIM-карты и без счетов за роуминг после поездки.",
    heroCta1 = "Выбрать тариф",
    heroCta2 = "Связаться с нами",
    heroPoster = "стран в покрытии",

    finderTitle = "Куда вы едете?",
    finderSub = "Цена eSIM на пакет данных",
    finderPlaceholder = "Например, Турция",
    finderNone = "Направление не нашли — напишите нам в Telegram, подключим вручную.",
    finderAll = "Все направления",

    stat1Value = "2 мин",
    stat1 = "на активацию",
    stat2Value = "0",
    stat2 = "переплат за роуминг",
    stat3Value = "24/7",
    stat3 = "поддержка в Telegram",

    tariffsTitle = "Куда вы едете?",
    tariffsSub = "Выберите страну — покажем пакеты и цены. QR-код приходит сразу после оплаты.",
    tariffsFrom = "от",

    destPopular = "Популярные направления",
    destAllCountries = "Все страны",
    destRegions = "Регионы и весь мир",
    destSearchPlaceholder = "Найдите страну или регион",
    destNoMatch = "Такое направление не нашли. Попробуйте другое написание или напишите нам в Telegram.",
    destLoading = "Загружаем направления…",
    destError = "Не удалось загрузить направления.",
    destBack = "Все направления",
    destPlanCountTemplate = "{n} {noun}",
    destChosenTemplate = "Пакеты: {destination}",
    destSeeAll = "Показать все страны",
    tariffsNote = "Цены в сумах, окончательные — скрытых комиссий нет.",
    tariffsAll = "Все направления →",
    tariffsLoading = "Загружаем тарифы…",
    tariffsEmpty = "В этой категории пока нет тарифов. Напишите нам в Telegram — подберём пакет вручную.",
    tariffsError = "Не удалось загрузить тарифы.",
    tariffsRetry = "Повторить",

    plansMetaTitle = "Все тарифы eSIM — Mint.uz",
    plansMetaDescription = "Полный каталог eSIM Mint.uz: локальные, региональные и глобальные пакеты данных. Поиск по направлению, цены в сумах.",
    plansTitle = "Все тарифы",
    plansLede = "Выберите страну или регион — покажем все пакеты и цены в сумах.",
    plansCountTemplate = "{n} {noun} в каталоге. Выберите направление, чтобы увидеть пакеты.",
    plansBack = "На главную",
    plansPrev = "Назад",
    plansNext = "Далее",
    plansPageTemplate = "Страница {0} из {1}",

    checkoutTitle = "Оформление",
    checkoutData = "Пакет данных",
    checkoutValidity = "Срок действия",
    checkoutCode = "Код тарифа",
    checkoutTotal = "К оплате",
    checkoutHandoff = "После оплаты eSIM выпустится автоматически — код установки появится на странице «Мои eSIM» в течение пары минут.",
    checkoutPay = "Оплатить через {gateway}",
    checkoutProceed = "Перейти к оплате",
    checkoutChoose = "Выберите способ оплаты",
    checkoutPaying = "Оформляем заказ…",
    checkoutUnavailable = "Онлайн-оплата сейчас недоступна. Оформим заказ в Telegram — там же примем оплату.",
    checkoutSignInNote = "Войдите по номеру телефона — eSIM привяжется к вашему аккаунту, и код установки будет всегда под рукой.",
    checkoutSignIn = "Войти и купить",
    checkoutOrderError = "Не удалось оформить заказ. Попробуйте ещё раз или напишите нам в Telegram.",
    checkoutTelegram = "Написать в Telegram",
    checkoutLoading = "Готовим оплату…",
    checkoutError = "Не удалось загрузить условия оплаты.",

    unitGb = "ГБ",
    unitMb = "МБ",
    currencyUzs = "сум",
    priceUnavailable = "Цена по запросу",
    dayForms = PluralForms("день", "дня", "дней"),
    planForms = PluralForms("тариф", "тарифа", "тарифов"),

    advTitle = "Почему покупают у Mint",
    advantages = listOf(
        SiteStrings.Advantage(
            "Активация за 2 минуты",
            "Оплатили — QR-код уже на почте. Сканируете камерой, и интернет работает.",
        ),
        SiteStrings.Advantage(
            "Без физической SIM",
            "Рабочий номер остаётся в телефоне — eSIM работает второй линией.",
        ),
        SiteStrings.Advantage(
            "Прозрачная цена",
            "Стоимость пакета видна до покупки. Никаких скрытых списаний после поездки.",
        ),
        SiteStrings.Advantage(
            "Поддержка 24/7",
            "Отвечаем в Telegram на узбекском, русском и английском.",
        ),
    ),

    ctaTitle1 = "Одна eSIM.",
    ctaTitle2 = "180 стран.",
    ctaSub = "Выберите направление — остальное займёт две минуты.",
    ctaBtn = "Открыть тарифы",

    footerAbout = "eSIM для поездок. ООО «Maxsus Trash», Ташкент.",
    footerC1 = "Компания",
    footerL1 = "О Mint",
    footerL2 = "Тарифы",
    footerC2 = "Помощь",
    footerL4 = "Как установить eSIM",
    footerL5 = "Совместимые телефоны",
    footerL6 = "Вопросы и ответы",
    footerC3 = "Оплата",
    footerRights = "© 2026 mintaccess.uz — все права защищены",
    footerTerms = "Публичная оферта",
    footerPrivacy = "Политика конфиденциальности",

    faqTitle = "Вопросы и ответы",
    faqMetaDescription = "Как работает eSIM, какие телефоны подходят, когда начинается отсчёт дней, чем платить и как оформить возврат — короткие ответы Mint.uz.",
    legalOffer = "Публичная оферта",
    legalPrivacy = "Политика конфиденциальности",
    legalClose = "Закрыть",
)

private val Uzbek = SiteStrings(
    metaTitle = "Mint.uz — sayohat uchun eSIM",
    metaDescription = "Chet el safarlari uchun eSIM: 180 davlatda internet, arzon narxdagi katta paketlar, rouming va plastik SIM-kartasiz.",

    navPlans = "Tariflar",
    navHow = "Qanday ishlaydi",
    navSupport = "Yordam",
    navCta = "eSIM tanlash",
    topPhoneLabel = "Qoʻllab-quvvatlash:",

    heroEyebrow = "Sayohat uchun eSIM",
    heroTitle1 = "Chet elda internet",
    heroTitle2 = "rouming toʻlovisiz",
    heroLede = "Tarifni uchishdan oldin ulang. Bitta eSIM 180 davlatda ishlaydi — jismoniy SIM-karta va sayohatdan keyingi rouming hisoblari yoʻq.",
    heroCta1 = "Tarifni tanlash",
    heroCta2 = "Biz bilan bogʻlanish",
    heroPoster = "davlatda qamrov",

    finderTitle = "Qayerga ketasiz?",
    finderSub = "Maʼlumot paketi uchun eSIM narxi",
    finderPlaceholder = "Masalan, Turkiya",
    finderNone = "Yoʻnalish topilmadi — Telegramda yozing, qoʻlda ulab beramiz.",
    finderAll = "Barcha yoʻnalishlar",

    stat1Value = "2 daq",
    stat1 = "aktivatsiya uchun",
    stat2Value = "0",
    stat2 = "rouming uchun ortiqcha toʻlov",
    stat3Value = "24/7",
    stat3 = "Telegramda yordam",

    tariffsTitle = "Qayerga ketasiz?",
    tariffsSub = "Davlatni tanlang — paketlar va narxlarni koʻrsatamiz. QR-kod toʻlovdan soʻng darhol keladi.",
    tariffsFrom = "dan",

    destPopular = "Ommabop yoʻnalishlar",
    destAllCountries = "Barcha davlatlar",
    destRegions = "Mintaqalar va butun dunyo",
    destSearchPlaceholder = "Davlat yoki mintaqani qidiring",
    destNoMatch = "Bunday yoʻnalish topilmadi. Boshqacha yozib koʻring yoki Telegramda yozing.",
    destLoading = "Yoʻnalishlar yuklanmoqda…",
    destError = "Yoʻnalishlarni yuklab boʻlmadi.",
    destBack = "Barcha yoʻnalishlar",
    destPlanCountTemplate = "{n} {noun}",
    destChosenTemplate = "Paketlar: {destination}",
    destSeeAll = "Barcha davlatlarni koʻrsatish",
    tariffsNote = "Narxlar soʻmda va yakuniy — yashirin komissiyalar yoʻq.",
    tariffsAll = "Barcha yoʻnalishlar →",
    tariffsLoading = "Tariflar yuklanmoqda…",
    tariffsEmpty = "Bu turkumda hozircha tarif yoʻq. Telegramda yozing — paketni qoʻlda tanlab beramiz.",
    tariffsError = "Tariflarni yuklab boʻlmadi.",
    tariffsRetry = "Qayta urinish",

    plansMetaTitle = "Barcha eSIM tariflari — Mint.uz",
    plansMetaDescription = "Mint.uz eSIM toʻliq katalogi: lokal, mintaqaviy va global maʼlumot paketlari. Yoʻnalish boʻyicha qidiruv, narxlar soʻmda.",
    plansTitle = "Barcha tariflar",
    plansLede = "Davlat yoki mintaqani tanlang — barcha paketlar va soʻmdagi narxlarni koʻrsatamiz.",
    plansCountTemplate = "Katalogda {n} {noun}. Paketlarni koʻrish uchun yoʻnalishni tanlang.",
    plansBack = "Bosh sahifaga",
    plansPrev = "Orqaga",
    plansNext = "Keyingi",
    plansPageTemplate = "{1} dan {0}-sahifa",

    checkoutTitle = "Rasmiylashtirish",
    checkoutData = "Maʼlumot paketi",
    checkoutValidity = "Amal qilish muddati",
    checkoutCode = "Tarif kodi",
    checkoutTotal = "Toʻlovga",
    checkoutHandoff = "Toʻlovdan soʻng eSIM avtomatik chiqariladi — oʻrnatish kodi bir necha daqiqada «Mening eSIM'larim» sahifasida paydo boʻladi.",
    checkoutPay = "{gateway} orqali toʻlash",
    checkoutProceed = "Toʻlovga oʻtish",
    checkoutChoose = "Toʻlov usulini tanlang",
    checkoutPaying = "Buyurtma rasmiylashtirilmoqda…",
    checkoutUnavailable = "Onlayn toʻlov hozircha ishlamayapti. Buyurtmani Telegramda rasmiylashtiramiz — toʻlovni ham oʻsha yerda qabul qilamiz.",
    checkoutSignInNote = "Telefon raqamingiz bilan kiring — eSIM hisobingizga bogʻlanadi va oʻrnatish kodi doim qoʻl ostida boʻladi.",
    checkoutSignIn = "Kirish va sotib olish",
    checkoutOrderError = "Buyurtmani rasmiylashtirib boʻlmadi. Qayta urinib koʻring yoki Telegram orqali yozing.",
    checkoutTelegram = "Telegramga yozish",
    checkoutLoading = "Toʻlov tayyorlanmoqda…",
    checkoutError = "Toʻlov shartlarini yuklab boʻlmadi.",

    unitGb = "GB",
    unitMb = "MB",
    currencyUzs = "soʻm",
    priceUnavailable = "Narx soʻrov boʻyicha",
    dayForms = PluralForms("kun", "kun", "kun"),
    planForms = PluralForms("tarif", "tarif", "tarif"),

    advTitle = "Nega Mint’dan sotib olishadi",
    advantages = listOf(
        SiteStrings.Advantage(
            "2 daqiqada aktivatsiya",
            "Toʻlovdan soʻng QR-kod pochtangizda. Kamera bilan skanerlaysiz — internet ishlaydi.",
        ),
        SiteStrings.Advantage(
            "Jismoniy SIM kerak emas",
            "Ish raqamingiz telefonda qoladi — eSIM ikkinchi liniya sifatida ishlaydi.",
        ),
        SiteStrings.Advantage(
            "Shaffof narx",
            "Paket narxi xariddan oldin koʻrinadi. Sayohatdan keyin yashirin yechimlar yoʻq.",
        ),
        SiteStrings.Advantage(
            "24/7 yordam",
            "Telegramda oʻzbek, rus va ingliz tillarida javob beramiz.",
        ),
    ),

    ctaTitle1 = "Bitta eSIM.",
    ctaTitle2 = "180 davlat.",
    ctaSub = "Yoʻnalishni tanlang — qolgani ikki daqiqa vaqt oladi.",
    ctaBtn = "Tariflarni ochish",

    footerAbout = "Sayohat uchun eSIM. «Maxsus Trash» MChJ, Toshkent.",
    footerC1 = "Kompaniya",
    footerL1 = "Mint haqida",
    footerL2 = "Tariflar",
    footerC2 = "Yordam",
    footerL4 = "eSIM oʻrnatish",
    footerL5 = "Mos telefonlar",
    footerL6 = "Savol-javob",
    footerC3 = "Toʻlov",
    footerRights = "© 2026 mintaccess.uz — barcha huquqlar himoyalangan",
    footerTerms = "Ommaviy oferta",
    footerPrivacy = "Maxfiylik siyosati",

    faqTitle = "Savol-javob",
    faqMetaDescription = "eSIM qanday ishlaydi, qaysi telefonlar mos keladi, kunlar qachondan sanaladi, nima bilan toʻlanadi va pul qanday qaytariladi — Mint.uz javoblari.",
    legalOffer = "Ommaviy oferta",
    legalPrivacy = "Maxfiylik siyosati",
    legalClose = "Yopish",
)

private val English = SiteStrings(
    metaTitle = "Mint.uz — eSIM for travel",
    metaDescription = "Travel eSIM: mobile data in 180 countries, large packages at a low price, no roaming charges and no plastic SIM cards.",

    navPlans = "Plans",
    navHow = "How it works",
    navSupport = "Support",
    navCta = "Find your eSIM",
    topPhoneLabel = "Support:",

    heroEyebrow = "eSIM for travel",
    heroTitle1 = "Data abroad",
    heroTitle2 = "without roaming",
    heroLede = "Set your plan up before you fly. One eSIM works in 180 countries — no physical SIM, no roaming bill waiting when you land back home.",
    heroCta1 = "Choose a plan",
    heroCta2 = "Contact us",
    heroPoster = "countries covered",

    finderTitle = "Where are you going?",
    finderSub = "eSIM price per data pack",
    finderPlaceholder = "Try Türkiye",
    finderNone = "No match — message us on Telegram and we'll set it up by hand.",
    finderAll = "All destinations",

    stat1Value = "2 min",
    stat1 = "to activate",
    stat2Value = "0",
    stat2 = "roaming surprises",
    stat3Value = "24/7",
    stat3 = "support on Telegram",

    tariffsTitle = "Where are you going?",
    tariffsSub = "Pick a country and we'll show the packages and prices. Your QR code arrives right after payment.",
    tariffsFrom = "from",

    destPopular = "Popular destinations",
    destAllCountries = "All countries",
    destRegions = "Regions and worldwide",
    destSearchPlaceholder = "Find a country or region",
    destNoMatch = "No such destination. Try another spelling, or message us on Telegram.",
    destLoading = "Loading destinations…",
    destError = "Couldn't load the destinations.",
    destBack = "All destinations",
    destPlanCountTemplate = "{n} {noun}",
    destChosenTemplate = "Packages: {destination}",
    destSeeAll = "Show all countries",
    tariffsNote = "Prices in UZS, final — no hidden fees.",
    tariffsAll = "All destinations →",
    tariffsLoading = "Loading plans…",
    tariffsEmpty = "Nothing in this category yet. Message us on Telegram and we'll put a package together.",
    tariffsError = "Couldn't load the plans.",
    tariffsRetry = "Try again",

    plansMetaTitle = "All eSIM plans — Mint.uz",
    plansMetaDescription = "The full Mint.uz eSIM catalogue: local, regional and global data packages. Search by destination, prices in UZS.",
    plansTitle = "All plans",
    plansLede = "Pick a country or region and we'll show every package, priced in UZS.",
    plansCountTemplate = "{n} {noun} in the catalogue. Pick a destination to see its packages.",
    plansBack = "Back to home",
    plansPrev = "Previous",
    plansNext = "Next",
    plansPageTemplate = "Page {0} of {1}",

    checkoutTitle = "Checkout",
    checkoutData = "Data package",
    checkoutValidity = "Valid for",
    checkoutCode = "Plan code",
    checkoutTotal = "Total",
    checkoutHandoff = "Your eSIM is issued automatically after payment — the installation code appears on My eSIMs within a couple of minutes.",
    checkoutPay = "Pay with {gateway}",
    checkoutProceed = "Continue to payment",
    checkoutChoose = "Choose how to pay",
    checkoutPaying = "Creating your order…",
    checkoutUnavailable = "Online payment isn't available right now. We'll take the order on Telegram and handle payment there.",
    checkoutSignInNote = "Sign in with your phone number — the eSIM is tied to your account, so the installation code is always to hand.",
    checkoutSignIn = "Sign in and buy",
    checkoutOrderError = "We couldn't create the order. Try again, or message us on Telegram.",
    checkoutTelegram = "Message us on Telegram",
    checkoutLoading = "Preparing payment…",
    checkoutError = "Couldn't load the payment options.",

    unitGb = "GB",
    unitMb = "MB",
    currencyUzs = "UZS",
    priceUnavailable = "Price on request",
    dayForms = PluralForms("day", "days", "days"),
    planForms = PluralForms("plan", "plans", "plans"),

    advTitle = "Why people buy from Mint",
    advantages = listOf(
        SiteStrings.Advantage(
            "Live in two minutes",
            "Pay and the QR code is in your inbox. Scan it with the camera and you're online.",
        ),
        SiteStrings.Advantage(
            "No physical SIM",
            "Your work number stays in the phone — the eSIM runs as a second line.",
        ),
        SiteStrings.Advantage(
            "Honest pricing",
            "You see the pack price before you buy. Nothing extra shows up after the trip.",
        ),
        SiteStrings.Advantage(
            "Support 24/7",
            "We answer on Telegram in Uzbek, Russian and English.",
        ),
    ),

    ctaTitle1 = "One eSIM.",
    ctaTitle2 = "180 countries.",
    ctaSub = "Pick a destination — the rest takes two minutes.",
    ctaBtn = "See the plans",

    footerAbout = "eSIM for travel. Maxsus Trash LLC, Tashkent.",
    footerC1 = "Company",
    footerL1 = "About Mint",
    footerL2 = "Plans",
    footerC2 = "Help",
    footerL4 = "Install an eSIM",
    footerL5 = "Compatible phones",
    footerL6 = "FAQ",
    footerC3 = "Payment",
    footerRights = "© 2026 mintaccess.uz — all rights reserved",
    footerTerms = "Public offer",
    footerPrivacy = "Privacy policy",

    faqTitle = "Questions and answers",
    faqMetaDescription = "How an eSIM works, which phones fit, when the days start counting, how to pay and how refunds work — short answers from Mint.uz.",
    legalOffer = "Public offer",
    legalPrivacy = "Privacy policy",
    legalClose = "Close",
)

fun stringsFor(locale: Locale): SiteStrings = when (locale) {
    Locale.RU -> Russian
    Locale.UZ -> Uzbek
    Locale.EN -> English
}
